import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import mixpanel from 'mixpanel-browser';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CrudHelper } from './helpers/crudHelper';
import { MapService } from '../services/map.service';
import { User } from '../shared/models/user.model';
import { ProfileStore } from '../profile/store/profile-store';
import { countries } from '../auth/sign-up/countries';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  properties;
  isUserPremium;
  private premiumStatusUserId: string;
  private chatVerifiedUserId: string;
  isChatVerified: boolean = false;
  private verificationCache: Map<
    string,
    { status: boolean; timestamp: number }
  > = new Map();
  private cacheExpirationTime = 30000; // 30 seconds cache
  private apiUrl = 'https://api.mixpanel.com';
  constructor(
    private fireStore: AngularFirestore,
    private profileStore: ProfileStore,
    private crudHelper: CrudHelper,
    private mapService: MapService,
    private router: Router,
    private http: HttpClient
  ) {}

  async getSubscriptionStatus(currentUser = getAuth().currentUser) {
    if (!currentUser) {
      return false;
    }

    if (this.isUserPremium && this.premiumStatusUserId === currentUser.uid) {
      return true;
    }

    const decodedToken = await currentUser.getIdTokenResult();
    const tokenHasPremium = decodedToken.claims.stripeRole === 'premium';
    if (tokenHasPremium) {
      this.isUserPremium = true;
      this.premiumStatusUserId = currentUser.uid;
      return true;
    }

    const hasActivePremium = await this.hasActiveFullPremiumSubscription(
      currentUser.uid
    );
    this.isUserPremium = hasActivePremium;
    this.premiumStatusUserId = currentUser.uid;
    return hasActivePremium;
  }

  async hasActiveFullPremiumSubscription(userId: string) {
    if (!userId) {
      return false;
    }

    const request = this.fireStore
      .collection('users')
      .doc(userId)
      .collection('subscriptions', ref =>
        ref
          .where('status', 'in', ['trialing', 'active'])
          .where('role', '==', 'premium')
      )
      .get();
    const subscriptionRef =
      typeof (request as any).toPromise === 'function'
        ? await (request as any).toPromise()
        : await (request as any);

    return subscriptionRef.docs.length > 0;
  }

  async getChatVerification() {
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      return false;
    }

    if (this.isChatVerifiedForUser(currentUser.uid)) {
      return true;
    }

    // Check if we have a cached result that's still valid
    const cachedVerification = this.verificationCache.get(currentUser.uid);
    if (
      cachedVerification &&
      Date.now() - cachedVerification.timestamp < this.cacheExpirationTime
    ) {
      return cachedVerification.status;
    }

    const userRef = this.fireStore.collection('users').doc(currentUser.uid);
    const userDoc = await userRef.get().toPromise();
    const userData = userDoc.data() as any;
    if (userDoc.exists && userData.isPersonalPremium) {
      this.markChatVerified(currentUser.uid);
      this.verificationCache.set(currentUser.uid, {
        status: true,
        timestamp: Date.now(),
      });
      return true;
    }

    if (await this.hasActiveChatSubscription(currentUser.uid)) {
      this.markChatVerified(currentUser.uid);
      this.verificationCache.set(currentUser.uid, {
        status: true,
        timestamp: Date.now(),
      });
      return true;
    }

    this.verificationCache.set(currentUser.uid, {
      status: this.isChatVerified,
      timestamp: Date.now(),
    });
    return this.isChatVerified;
  }

  async getMembership(userId) {
    if (!userId) {
      return false;
    }

    // Check if we have a cached result that's still valid
    const cachedVerification = this.verificationCache.get(userId);
    if (
      cachedVerification &&
      Date.now() - cachedVerification.timestamp < this.cacheExpirationTime
    ) {
      return cachedVerification.status;
    }

    if (
      userId === getAuth().currentUser?.uid &&
      this.isChatVerifiedForUser(userId)
    ) {
      return true;
    }

    const userRef = this.fireStore.collection('users').doc(userId);
    const userDoc = await userRef.get().toPromise();
    const userData = userDoc.data() as any;
    if (userDoc.exists && userData.isPersonalPremium) {
      if (userId === getAuth().currentUser?.uid) {
        this.markChatVerified(userId);
      }
      this.verificationCache.set(userId, {
        status: true,
        timestamp: Date.now(),
      });
      return true;
    }

    if (await this.hasActiveChatSubscription(userId)) {
      if (userId === getAuth().currentUser?.uid) {
        this.markChatVerified(userId);
      }
      this.verificationCache.set(userId, {
        status: true,
        timestamp: Date.now(),
      });
      return true;
    }

    this.verificationCache.set(userId, {
      status: false,
      timestamp: Date.now(),
    });
    return userId === getAuth().currentUser?.uid
      ? this.isChatVerifiedForUser(userId)
      : false;
  }

  private async hasActiveChatSubscription(userId: string) {
    const hasFullSubscription = await this.hasActiveSubscription(
      userId,
      'subscriptions'
    );
    if (hasFullSubscription) {
      return true;
    }

    return this.hasActiveSubscription(userId, 'basic-subscriptions');
  }

  private async hasActiveSubscription(
    userId: string,
    collectionName: string
  ) {
    const subscriptionRef = await this.fireStore
      .collection('users')
      .doc(userId)
      .collection(collectionName, ref =>
        ref.where('status', 'in', ['trialing', 'active'])
      )
      .get()
      .toPromise();

    return subscriptionRef.docs.length > 0;
  }

  trackEvent(event, userCredential, user, country) {
    this.properties = {
      id: userCredential.user.uid,
      country,
      region: country.region,
      city: user.address.city,
      email: user.email,
      'marketing-accept': user.isMailingEnabled,
      'premium-active': false,
    };

    mixpanel.track(event, this.properties);
  }

  getCurrentUser() {
    return getAuth().currentUser;
  }

  isUserAuth() {
    return getAuth().currentUser;
  }

  inviteMerfriend = async (recipientEmail, message, sender) => {
    const functions = getFunctions();
    const sendMail = httpsCallable(functions, 'sendMail');
    const data = await sendMail({ recipientEmail, message, sender });
    if (data) {
      return data;
    } else {
      throw 'Firebase error';
    }
  };

  signUp = async user => {
    const { address, username, email, password, isMailingEnabled } = user;
    const country = countries.find(country => country.name === address.country);
    const location = this.mapService.createCoordinates(address.googleMapLink);

    const auth = getAuth();
    const functions = getFunctions();

    const isUsernameTaken = httpsCallable(functions, 'isUsernameTaken');

    const { data } = await isUsernameTaken({ username });
    if (data) {
      throw 'auth/username-already-exist';
    }
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const { uid } = userCredential.user;
    this.fireStore.doc(`users/${uid}`).set({
      uid,
      email,
      username,
      country,
      city: null,
      postalCode: null,
      location,
      profilePicture: null,
      gender: 'Other',
      mermaidExpLvl: null,
      mersonaName: null,
      galleryVisibility: true,
      isMailingEnabled,
      platform: Capacitor.getPlatform(),
    });
    mixpanel.init(environment.mixpanelToken, { debug: true });
    mixpanel.identify(uid);
    this.trackEvent('New User', userCredential, user, country);

    mixpanel.people.set({
      $region: country.region,
      $city: address.city,
      $email: email,
      $country: country,
      $createdAt: new Date(),
      'marketing-accept': isMailingEnabled,
      'premium-active': false,
    });
  };

  signIn = async ({ email, password, rememberMe }) => {
    const auth = getAuth();
    const sessionType = browserLocalPersistence;

    await setPersistence(auth, sessionType);
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential;
  };

  signOut = async () => {
    this.clearVerificationCache();
    await signOut(getAuth());
    this.router.navigate(['/'], { replaceUrl: true });
    this.profileStore.logOut();
  };

  deleteProfile = async () => {
    const functions = getFunctions();
    const isDeleted = httpsCallable(functions, 'deleteUser');
    try {
      const data = await isDeleted();
      return data;
    } catch (error) {
      throw 'Firebase error';
    }
  };

  async sendResetEmail({ email }) {
    const auth = getAuth();
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent');
    } catch (error) {
      console.log(error.message);
    }
  }

  getUsers = () => {
    return this.crudHelper.getHelper({ collectionName: 'users' });
  };

  getUserById = (id: string): Observable<User> => {
    return this.fireStore
      .collection('users', ref => ref.where('id', '==', id))
      .snapshotChanges()
      .pipe(
        map(user => {
          const userData = user[0].payload.doc.data() as User;
          return {
            id: user[0].payload.doc.id,
            ...userData,
          };
        })
      );
  };

  getUserByUid = uid => {
    return this.fireStore
      .collection('users', ref => ref.where('uid', '==', uid))
      .snapshotChanges()
      .pipe(
        map(user => {
          return Object.assign(
            {
              id: user[0].payload.doc.id,
            },
            user[0].payload.doc.data()
          );
        }),
        catchError(async e => {
          console.log(`User with uid ${uid} not found or not exist`);
          return { id: '' };
        })
      );
  };

  getUserBySearch = async (start, end) => {
    return await this.crudHelper.searchHelper({
      collectionName: 'users',
      searchField: 'username',
      limit: 50,
      start,
      end,
    });
  };

  setPaidPremium = (userId?: string) => {
    this.isUserPremium = true;

    const verifiedUserId = userId || getAuth().currentUser?.uid;
    if (verifiedUserId) {
      this.premiumStatusUserId = verifiedUserId;
      this.markChatVerified(verifiedUserId);
      this.verificationCache.set(verifiedUserId, {
        status: true,
        timestamp: Date.now(),
      });
    }
  };

  // Clear the verification cache to force fresh checks
  clearVerificationCache() {
    this.verificationCache.clear();
    // Reset the verification status as well to force a complete re-check
    this.isChatVerified = false;
    this.chatVerifiedUserId = null;
  }

  isChatVerifiedForUser(userId: string) {
    return !!userId && this.isChatVerified && this.chatVerifiedUserId === userId;
  }

  private markChatVerified(userId: string) {
    this.isChatVerified = true;
    this.chatVerifiedUserId = userId;
  }
}

